<template>
  <div
    v-if="visible"
    class="result-reference-panel"
    role="region"
    :aria-label="$t('ผลรางวัลจากแหล่งอ้างอิง')"
    :aria-busy="loading"
  >
    <div class="reference-heading">
      <span>{{ $t('ผลอ้างอิง') }}</span>
      <template v-if="snapshot.sources.length === 1">
        <a
          :href="snapshot.sources[0].url"
          target="_blank"
          rel="noopener noreferrer"
        >{{ snapshot.sources[0].name }} ↗</a>
        <template v-if="snapshot.sources[0].status === ReferenceSourceStatus.Available">
          <span
            v-for="row in rows"
            :key="row.label"
            class="reference-number"
            :class="{ 'reference-danger': isMismatch(snapshot.sources[0], row) }"
          >
            {{ $t(row.label) }} <strong>{{ matrixValue(snapshot.sources[0].numbers, row) }}</strong>
          </span>
        </template>
        <span
          v-else-if="isLoading(snapshot.sources[0])"
          class="reference-secondary"
        >{{ $t('กำลังดึงผล') }}</span>
        <span
          v-else
          class="reference-warning"
        >{{ $t(statusLabel(snapshot.sources[0].status)) }}</span>
      </template>
      <template v-else-if="snapshot.sources.length">
        <span
          v-if="conflictCount"
          class="reference-summary reference-danger"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="14"
          />
          {{ $t('แหล่งให้ผลต่างกัน {count} รางวัล', { count: conflictCount }) }}
        </span>
        <span
          v-else-if="inputMismatch"
          class="reference-summary reference-danger"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="14"
          />
          {{ $t('เลขที่กรอกไม่ตรงกับแหล่งอ้างอิง') }}
        </span>
        <span
          v-else-if="availableSources.length > 1"
          class="reference-summary reference-success"
        >
          <feather-icon
            icon="CheckIcon"
            size="14"
          />
          {{ $t('{count} แหล่งตรงกัน', { count: availableSources.length }) }}
        </span>
        <span
          v-else-if="availableSources.length === 1"
          class="reference-secondary"
        >{{ $t('มีผล {count} จาก {total} แหล่ง', { count: 1, total: snapshot.sources.length }) }}</span>
      </template>
      <span
        v-else-if="!loading"
        class="reference-secondary"
      >{{ $t('ยังไม่มีผลอ้างอิง') }}</span>
      <b-button
        variant="link"
        size="sm"
        class="reference-refresh p-25"
        :disabled="loading"
        :aria-label="$t(loading ? 'กำลังดึงผลอ้างอิง' : 'รีเฟรช')"
        :title="$t('รีเฟรช')"
        @click="refresh"
      >
        <b-spinner
          v-if="loading"
          small
          aria-hidden="true"
        />
        <feather-icon
          v-else
          icon="RefreshCwIcon"
          size="14"
        />
      </b-button>
      <b-button
        v-if="availableSources.length && !conflictCount && canUse(availableSources[0])"
        variant="link"
        size="sm"
        class="reference-fill"
        :disabled="pulling"
        @click="fill(availableSources[0].numbers)"
      >
        <feather-icon
          icon="CornerLeftUpIcon"
          size="14"
        />
        {{ $t('ใช้เลขนี้') }}
      </b-button>
      <div class="reference-actions">
        <b-button
          variant="link"
          size="sm"
          class="p-0"
          @click="detailsVisible = true"
        >{{ $t('ดูรายละเอียด') }}</b-button>
      </div>
    </div>
    <small
      v-if="error"
      class="d-block reference-warning mt-25"
    >{{ error }}</small>
    <div
      v-if="snapshot.sources.length > 1"
      class="table-responsive reference-comparison-box"
      tabindex="0"
      :aria-label="$t('ผลรางวัลจากแหล่งอ้างอิง')"
    >
      <table class="table table-sm table-borderless reference-comparison mb-0">
        <thead>
          <tr>
            <th scope="col">
              {{ $t('แหล่งอ้างอิงผลหวย') }}
            </th>
            <th
              v-for="row in rows"
              :key="row.label"
              scope="col"
            >
              {{ $t(row.label) }}
            </th>
            <th
              v-if="canFill && conflictCount"
              scope="col"
            >
              <span class="sr-only">{{ $t('ใช้เลขจากแหล่งนี้') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="source in snapshot.sources"
            :key="source.id"
          >
            <th scope="row">
              <a
                :href="source.url"
                target="_blank"
                rel="noopener noreferrer"
              >{{ source.name }} ↗</a>
            </th>
            <template v-if="source.status === ReferenceSourceStatus.Available">
              <td
                v-for="row in rows"
                :key="row.label"
                class="reference-number"
                :class="{ 'reference-danger': isMismatch(source, row) }"
              >{{ matrixValue(source.numbers, row) }}</td>
            </template>
            <td
              v-else-if="isLoading(source)"
              :colspan="rows.length"
              class="reference-secondary"
            >
              {{ $t('กำลังดึงผล') }}
            </td>
            <td
              v-else
              :colspan="rows.length"
              class="reference-warning"
            >
              {{ $t(statusLabel(source.status)) }}
            </td>
            <td
              v-if="canFill && conflictCount"
              class="text-right"
            >
              <b-button
                v-if="canUse(source)"
                variant="link"
                size="sm"
                class="reference-fill"
                :disabled="pulling"
                :aria-label="`${$t('ใช้เลขจากแหล่งนี้')} · ${source.name}`"
                @click="fill(source.numbers)"
              >
                <feather-icon
                  icon="CornerLeftUpIcon"
                  size="14"
                />
                {{ $t('ใช้เลขจากแหล่งนี้') }}
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <b-modal
      v-model="detailsVisible"
      :title="$t('ผลรางวัลจากแหล่งอ้างอิง')"
      centered
      scrollable
      modal-class="reference-details-modal"
      body-class="reference-details-body"
      footer-class="reference-details-footer"
    >
      <div>
        <div
          v-if="snapshot.huayName"
          class="reference-details-lottery"
        >
          {{ snapshot.huayName }}
        </div>
        <div class="reference-details-date">
          {{ $t('งวดวันที่') }} {{ displayDate(snapshot.drawDate) }}
        </div>
      </div>
      <div
        v-if="error"
        class="reference-details-alert"
        role="alert"
      >
        <feather-icon
          icon="AlertTriangleIcon"
          size="16"
        />
        <span>{{ error }}</span>
      </div>
      <p
        v-if="!snapshot.sources.length && !loading && !error"
        class="reference-details-empty"
      >
        {{ $t('ยังไม่มีผลอ้างอิง') }}
      </p>
      <ul
        v-if="snapshot.sources.length"
        class="reference-chips"
        :aria-label="$t('แหล่งอ้างอิงผลหวย')"
      >
        <li
          v-for="source in snapshot.sources"
          :key="source.id"
        >
          <a
            class="reference-chip"
            :class="`is-${chipState(source)}`"
            :href="source.url"
            :title="chipTitle(source)"
            target="_blank"
            rel="noopener noreferrer"
          >
            <feather-icon
              v-if="chipState(source) !== 'neutral'"
              :icon="chipState(source) === 'agree' ? 'CheckIcon' : 'AlertTriangleIcon'"
              size="12"
            />
            {{ source.name }}
            <span class="reference-chip-meta">· {{ chipMeta(source) }}</span>
          </a>
        </li>
      </ul>
      <div
        v-if="conflictCount"
        class="reference-details-alert"
        role="status"
      >
        <feather-icon
          icon="AlertTriangleIcon"
          size="16"
        />
        <span>{{ $t('แหล่งอ้างอิงให้ผลต่างกัน {count} รางวัล ตรวจกับเว็บไซต์ก่อนเลือกใช้', { count: conflictCount }) }}</span>
      </div>
      <p
        v-else-if="snapshot.sources.length && !availableSources.length && !loading"
        class="reference-details-empty"
      >
        {{ $t('ยังไม่มีแหล่งที่ให้ผลได้ กรุณาตรวจเลขด้วยตนเอง') }}
      </p>
      <dl
        v-if="availableSources.length"
        class="reference-results"
      >
        <div
          v-for="item in comparison"
          :key="item.row.label"
          class="reference-result"
          :class="{ 'is-conflict': item.groups.length > 1 }"
        >
          <dt>
            <feather-icon
              v-if="item.groups.length > 1"
              icon="AlertTriangleIcon"
              size="13"
            />
            {{ $t(item.row.label) }}
          </dt>
          <dd v-if="item.groups.length === 1">
            <span class="reference-result-value">{{ item.groups[0].value }}</span>
            <span
              v-if="inputState(item) !== 'empty'"
              class="reference-result-input"
              :class="`is-${inputState(item)}`"
            >
              <template v-if="inputState(item) === 'match'">
                <feather-icon
                  icon="CheckIcon"
                  size="12"
                />
                {{ $t('ตรงกับที่กรอก') }}
              </template>
              <template v-else>{{ $t('กรอกไว้') }} {{ matrixValue(huayData, item.row) }}</template>
            </span>
          </dd>
          <dd v-else>
            <span
              v-for="group in item.groups"
              :key="group.value"
              class="reference-result-variant"
            >
              <span class="reference-result-value">{{ group.value }}</span>
              <small>{{ group.sources.map(source => source.name).join(', ') }}</small>
            </span>
            <span
              v-if="inputState(item) !== 'empty'"
              class="reference-result-input"
            >{{ $t('กรอกไว้') }} {{ matrixValue(huayData, item.row) }}</span>
          </dd>
        </div>
      </dl>
      <p
        v-for="source in availableSources.filter(v => v.originalNumber)"
        :key="`original-${source.id}`"
        class="reference-details-note"
      >
        {{ source.name }}: {{ $t('แปลงจากผลเลข 4 ตัว') }} <strong>{{ source.originalNumber }}</strong>
      </p>
      <div
        v-if="canFill && availableSources.length"
        class="reference-details-fill"
      >
        <template v-if="!conflictCount">
          <b-button
            v-if="canUse(availableSources[0])"
            variant="primary"
            size="sm"
            :disabled="pulling"
            @click="fill(availableSources[0].numbers)"
          >
            <feather-icon
              icon="CornerLeftUpIcon"
              size="14"
              class="mr-25"
            />
            {{ $t('ใช้เลขนี้') }}
          </b-button>
          <span
            v-else
            class="reference-details-used"
          >
            <feather-icon
              icon="CheckCircleIcon"
              size="14"
            />
            {{ $t('ช่องกรอกใช้เลขนี้แล้ว') }}
          </span>
        </template>
        <template v-else>
          <span class="reference-details-pick">{{ $t('เลือกใช้เลขจาก') }}</span>
          <template v-for="source in availableSources">
            <b-button
              v-if="canUse(source)"
              :key="source.id"
              variant="outline-secondary"
              size="sm"
              class="reference-outline-button"
              :disabled="pulling"
              :aria-label="`${$t('ใช้เลขจากแหล่งนี้')} · ${source.name}`"
              @click="fill(source.numbers)"
            >
              {{ source.name }}
            </b-button>
            <span
              v-else
              :key="source.id"
              class="reference-details-used"
            >
              <feather-icon
                icon="CheckIcon"
                size="12"
              />
              {{ source.name }}
            </span>
          </template>
        </template>
      </div>
      <template #modal-footer>
        <b-button
          variant="link"
          size="sm"
          class="reference-history-link"
          @click="showHistory"
        >
          <feather-icon
            icon="ClockIcon"
            size="14"
          />
          {{ $t('ประวัติการยืนยันผล') }}
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          class="reference-outline-button"
          :disabled="loading"
          @click="refresh"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-25"
            aria-hidden="true"
          />
          <feather-icon
            v-else
            icon="RefreshCwIcon"
            size="14"
            class="mr-25"
          />
          {{ $t(loading ? 'กำลังดึงผลอ้างอิง' : 'ดึงผลใหม่') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal
      v-model="historyVisible"
      :title="$t('ประวัติการยืนยันผล')"
      size="lg"
      centered
      scrollable
      ok-only
      :ok-title="$t('ปิด')"
      ok-variant="outline-secondary"
      modal-class="reference-history-modal"
      body-class="reference-details-body"
    >
      <p
        v-if="!records.length"
        class="reference-details-empty"
      >
        {{ $t('ยังไม่มีประวัติการยืนยันในรอบนี้') }}
      </p>
      <ol
        v-else
        class="reference-history"
      >
        <li
          v-for="record in records"
          :key="record.id"
          class="reference-history-item"
        >
          <div class="reference-history-head">
            <span class="reference-history-action">{{ $t(record.action === ReferenceAction.Dispatch ? 'จ่ายเครดิต' : 'คำนวณราคา') }}</span>
            <span class="reference-history-actor">{{ record.actor }}</span>
            <time
              class="reference-history-time"
              :datetime="record.confirmedAt"
            >{{ displayTime(record.confirmedAt) }}</time>
          </div>
          <dl class="reference-history-numbers">
            <div
              v-for="row in rows"
              :key="row.label"
            >
              <dt>{{ $t(row.label) }}</dt>
              <dd>{{ matrixValue(record.numbers, row) }}</dd>
            </div>
          </dl>
          <p
            v-if="record.reason"
            class="reference-history-reason"
          >
            <span>{{ $t('เหตุผล') }}</span>
            {{ record.reason }}
          </p>
          <ul
            v-if="record.sources && record.sources.length"
            class="reference-chips"
            :aria-label="$t('แหล่งอ้างอิงผลหวย')"
          >
            <li
              v-for="source in record.sources"
              :key="source.id"
            >
              <span
                class="reference-chip"
                :class="`is-${recordChipState(record, source)}`"
              >
                <feather-icon
                  :icon="{ agree: 'CheckIcon', mismatch: 'XIcon' }[recordChipState(record, source)] || 'AlertTriangleIcon'"
                  size="12"
                />
                {{ source.name }}
                <span
                  v-if="recordChipState(record, source) !== 'agree'"
                  class="reference-chip-meta"
                >· {{ recordChipState(record, source) === 'mismatch'
                  ? $t('ไม่ตรง {count} รางวัล', { count: rows.filter(row => !rowMatches(record.numbers, source.numbers, row)).length })
                  : $t(statusLabel(source.status)) }}</span>
              </span>
            </li>
          </ul>
        </li>
      </ol>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton, BModal, BSpinner,
} from 'bootstrap-vue'
import {
  referenceRequest, referenceRows, rowMatches, pairValue, dateLocale, drawDateLabel, statusLabel, supportedLottery, confirmReference,
} from '@/service/resultReference'
import { ReferenceSourceStatus, ReferenceAction } from '@/service/resultReferenceTypes'

export default {
  components: {
    BButton, BModal, BSpinner,
  },
  props: {
    huayId: { type: String, required: true },
    roundId: { type: String, default: '' },
    history: { type: Boolean, default: false },
    huayData: { type: Object, required: true },
    canFill: { type: Boolean, default: false },
  },
  data: () => ({
    ReferenceSourceStatus,
    ReferenceAction,
    snapshot: { enabled: false, sources: [] },
    roundLoading: false,
    // Sources whose request is still in flight; every other row already shows its own answer.
    pendingIds: [],
    pulling: false,
    retryTimer: null,
    error: '',
    sequence: 0,
    records: [],
    detailsVisible: false,
    historyVisible: false,
  }),
  computed: {
    visible() { return this.snapshot.enabled || !!this.error },
    loading() { return this.roundLoading || this.pendingIds.length > 0 },
    rows() { return referenceRows(this.huayId) },
    contextKey() { return `${this.huayId}/${this.roundId}/${this.history}` },
    query() { return `roundId=${encodeURIComponent(this.roundId)}&history=${this.history}` },
    availableSources() { return this.snapshot.sources.filter(source => source.status === ReferenceSourceStatus.Available) },
    comparison() {
      return this.rows.map(row => {
        const groups = []
        this.availableSources.forEach(source => {
          const value = this.matrixValue(source.numbers, row)
          const group = groups.find(item => item.value === value)
          if (group) group.sources.push(source)
          else groups.push({ value, sources: [source] })
        })
        return { row, groups }
      })
    },
    conflictCount() { return this.comparison.filter(item => item.groups.length > 1).length },
    inputMismatch() {
      return !this.conflictCount && this.availableSources.length > 0
        && this.rows.some(row => this.isMismatch(this.availableSources[0], row))
    },
    // drawDate is a calendar date (yyyy-MM-dd); results are expected to appear during that local day.
    isDrawDay() {
      const [year, month, day] = String(this.snapshot.drawDate || '').split('-').map(Number)
      const today = new Date()
      return year === today.getFullYear() && month === today.getMonth() + 1 && day === today.getDate()
    },
  },
  watch: {
    contextKey: {
      immediate: true,
      handler() {
        this.snapshot = { enabled: false, sources: [] }
        this.detailsVisible = false
        this.historyVisible = false
        this.refresh()
      },
    },
  },
  beforeDestroy() {
    this.sequence += 1
    clearTimeout(this.retryTimer)
  },
  methods: {
    rowMatches,
    statusLabel,
    matrixValue: pairValue,
    displayTime(value) {
      return value ? new Date(value).toLocaleString(dateLocale(this.$i18n.locale), { dateStyle: 'medium', timeStyle: 'short' }) : '—'
    },
    displayDate(value) { return drawDateLabel(value, this.$i18n.locale) },
    displayClock(value) {
      return value ? new Date(value).toLocaleTimeString(dateLocale(this.$i18n.locale), { hour: '2-digit', minute: '2-digit' }) : '—'
    },
    isLoading(source) { return this.pendingIds.includes(source.id) },
    // A confirmation record is compared with the numbers that were confirmed.
    recordChipState(record, source) {
      if (source.status !== ReferenceSourceStatus.Available) return 'unavailable'
      return this.rows.every(row => rowMatches(record.numbers, source.numbers, row)) ? 'agree' : 'mismatch'
    },
    // Sources are grouped by identical values only; a larger group never overrides a disagreeing source.
    chipState(source) {
      if (this.isLoading(source)) return 'neutral'
      if (source.status !== ReferenceSourceStatus.Available) return 'unavailable'
      return this.conflictCount ? 'neutral' : 'agree'
    },
    chipMeta(source) {
      if (this.isLoading(source)) return this.$t('กำลังดึงผล')
      return source.status === ReferenceSourceStatus.Available ? this.displayClock(source.fetchedAt) : this.$t(statusLabel(source.status))
    },
    chipTitle(source) {
      if (this.isLoading(source)) return this.$t('กำลังดึงผลอ้างอิง')
      const fetched = `${this.$t('ดึงล่าสุด')} ${this.displayTime(source.fetchedAt)}`
      return source.status === ReferenceSourceStatus.Available ? fetched : `${this.$t(this.statusHint(source.status))} · ${fetched}`
    },
    inputState(item) {
      return this.prizeState(item.groups[0].sources[0], item.row)
    },
    prizeState(source, row) {
      if (row.keys.every(key => !this.huayData[key])) return 'empty'
      if (rowMatches(this.huayData, source.numbers, row)) return 'match'
      return this.isMismatch(source, row) ? 'mismatch' : 'partial'
    },
    statusHint(status) {
      return {
        [ReferenceSourceStatus.Pending]: 'เว็บไซต์ยังประกาศผลไม่ครบ ลองดึงผลใหม่อีกครั้งภายหลัง',
        [ReferenceSourceStatus.WrongDraw]: 'เว็บไซต์ยังแสดงผลของงวดอื่น',
      }[status] || 'เชื่อมต่อเว็บไซต์ไม่ได้ ลองดึงผลใหม่อีกครั้ง'
    },
    isMismatch(source, row) {
      return source.status === ReferenceSourceStatus.Available && source.numbers
        && row.keys.every(key => String(this.huayData[key] || '').length > 0
          && String(this.huayData[key]).length === String(source.numbers[key] || '').length)
        && !rowMatches(this.huayData, source.numbers, row)
    },
    // Offer the shortcut only while the inputs still differ from this source.
    canUse(source) {
      return this.canFill && source.status === ReferenceSourceStatus.Available && !!source.numbers
        && !this.rows.every(row => rowMatches(this.huayData, source.numbers, row))
    },
    inputKey() { return this.rows.flatMap(row => row.keys).map(key => this.huayData[key] || '').join('|') },
    // Lists the enabled sources, then loads each one on its own so a slow website never holds up the others.
    async refresh(onSource) {
      this.sequence += 1
      const { sequence } = this
      clearTimeout(this.retryTimer)
      this.error = ''
      this.pendingIds = []
      if (!supportedLottery(this.huayId) || !this.roundId) { this.$emit('enabled', false); return }
      this.roundLoading = true
      let round = null
      try {
        round = await referenceRequest('get', `round/${encodeURIComponent(this.huayId)}?${this.query}`)
      } catch (error) {
        if (sequence === this.sequence) {
          this.error = error.message
          this.snapshot = { enabled: false, sources: [] }
        }
      } finally {
        if (sequence === this.sequence) this.roundLoading = false
      }
      if (sequence !== this.sequence) return
      if (round) this.snapshot = { ...round, sources: round.sources.map(source => ({ ...source, status: null, numbers: null })) }
      this.$emit('enabled', this.visible)
      if (!round || !round.enabled) return
      await this.loadSources(round.sources.map(source => source.id), sequence, onSource)
      if (sequence === this.sequence) this.scheduleRetry()
    },
    async loadSources(ids, sequence, onSource) {
      this.pendingIds = [...this.pendingIds, ...ids.filter(id => !this.pendingIds.includes(id))]
      await Promise.all(ids.map(async id => {
        let result
        try {
          result = await referenceRequest('get', `sources/${encodeURIComponent(this.huayId)}/${encodeURIComponent(id)}?${this.query}`)
        } catch (error) {
          if (sequence === this.sequence) this.error = error.message
          result = { status: ReferenceSourceStatus.Unavailable, numbers: null }
        }
        if (sequence !== this.sequence) return
        const index = this.snapshot.sources.findIndex(source => source.id === id)
        if (index >= 0) this.snapshot.sources.splice(index, 1, { ...this.snapshot.sources[index], ...result })
        this.pendingIds = this.pendingIds.filter(pending => pending !== id)
        if (onSource && index >= 0) onSource(this.snapshot.sources[index])
      }))
    },
    // On the draw day of the current round, re-request only the sources still without a result, while the tab is visible.
    scheduleRetry() {
      clearTimeout(this.retryTimer)
      if (this.history || !this.snapshot.enabled || !this.isDrawDay
        || this.snapshot.sources.every(source => source.status === ReferenceSourceStatus.Available)) return
      const { sequence } = this
      this.retryTimer = setTimeout(async () => {
        if (sequence !== this.sequence) return
        const ids = this.snapshot.sources
          .filter(source => source.status !== ReferenceSourceStatus.Available && !this.isLoading(source))
          .map(source => source.id)
        if (document.visibilityState === 'visible' && ids.length) await this.loadSources(ids, sequence)
        if (sequence === this.sequence) this.scheduleRetry()
      }, 60000)
    },
    async fillBest() {
      if (this.pulling) return
      const context = this.contextKey
      // Numbers typed while the pull is running belong to the operator; never overwrite them.
      const typed = this.inputKey()
      let filled = null
      this.pulling = true
      this.$emit('pulling', true)
      try {
        // Fill from the first complete source; later disagreeing sources show up as a conflict, and the
        // confirmation check still compares every source before calculation or payout.
        await this.refresh(source => {
          if (filled || source.status !== ReferenceSourceStatus.Available || !source.numbers || this.inputKey() !== typed) return
          filled = source
          this.fill(source.numbers)
        })
      } finally {
        this.pulling = false
        this.$emit('pulling', false)
      }
      if (context !== this.contextKey) return
      if (!this.availableSources.length) {
        await this.$swal({ icon: 'warning', text: this.$t('ยังไม่มีผลอ้างอิงที่ใช้เติมเลขได้ กรุณาตรวจเลขด้วยตนเอง') })
      } else if (!filled) {
        await this.$swal({ icon: 'info', text: this.$t('ช่องกรอกถูกแก้ไขระหว่างดึงผล จึงไม่ได้เติมเลขทับ กรุณาตรวจและเลือกใช้เลขเอง') })
        this.detailsVisible = true
      } else if (this.conflictCount) {
        await this.$swal({ icon: 'warning', text: this.$t('แหล่งอ้างอิงให้ผลต่างกัน เลขที่เติมมาจาก {source} กรุณาตรวจและเลือกแหล่งที่ต้องการ', { source: filled.name }) })
        this.detailsVisible = true
      }
    },
    fill(numbers) {
      if (!this.canFill) return
      this.$emit('fill', numbers)
      this.detailsVisible = false
    },
    async confirm(action, numbers) {
      if (!supportedLottery(this.huayId)) return { handled: false }
      const context = this.contextKey
      const check = await referenceRequest('post', 'check', {
        huayId: this.huayId, roundId: this.roundId, history: this.history, action, numbers,
      })
      if (context !== this.contextKey) throw new Error(this.$t('รอบหวยเปลี่ยนไป กรุณาตรวจและยืนยันอีกครั้ง'))
      if (!check.enabled) return { handled: false }
      // The check is the newest full snapshot; results still in flight belong to an older one.
      this.sequence += 1
      this.pendingIds = []
      this.error = ''
      this.snapshot = check
      this.$emit('enabled', true)
      this.scheduleRetry()
      const confirmation = await confirmReference(this, check)
      if (context !== this.contextKey) throw new Error(this.$t('รอบหวยเปลี่ยนไป กรุณาตรวจและยืนยันอีกครั้ง'))
      return { handled: true, confirmation }
    },
    async showHistory() {
      try {
        this.records = await referenceRequest('get', `history/${encodeURIComponent(this.huayId)}?roundId=${encodeURIComponent(this.roundId)}`)
        this.historyVisible = true
      } catch (error) { this.error = error.message }
    },
  },
}
</script>

<style lang="scss" scoped>
.result-reference-panel {
  border-top: 1px solid var(--hdg-border);
  padding-top: 0.5rem;
  font-size: 0.857rem;
}
.reference-heading,
.reference-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.reference-heading { flex-wrap: wrap; }
.reference-actions { margin-left: auto; flex-shrink: 0; gap: 0.5rem; }
.reference-actions .btn,
.reference-refresh { color: inherit; }
.reference-refresh { margin-left: -0.5rem; }
.reference-actions .btn { font-weight: 400; text-decoration: underline; text-underline-offset: 0.15em; }
.reference-fill { display: inline-flex; align-items: center; gap: 0.25rem; padding: 0; font-size: inherit; font-weight: 400; white-space: nowrap; }
.reference-fill,
.reference-fill:hover,
.reference-fill:focus { color: var(--reference-link); }
.reference-danger { color: var(--reference-danger); }
.reference-warning { color: var(--reference-warning); }
// The dark theme sets `.dark-layout .table tbody tr:not([class*=table-]) td` (0,3,3), which outranks the classes above.
.table.reference-comparison tbody tr td.reference-danger { color: var(--reference-danger); }
.table.reference-comparison tbody tr td.reference-warning { color: var(--reference-warning); }
.table.reference-comparison tbody tr td.reference-secondary { color: var(--hdg-body); }
.reference-success { color: var(--reference-success); }
.reference-secondary { color: var(--hdg-body); }
.reference-summary { display: inline-flex; align-items: center; gap: 0.25rem; white-space: nowrap; }
.reference-heading a,
.reference-comparison a { color: inherit; text-decoration: underline; text-underline-offset: 0.15em; }
.reference-number { white-space: nowrap; font-variant-numeric: tabular-nums; }
// The header band reads as part of a box rather than a stripe floating above unbounded rows.
.reference-comparison-box {
  margin-top: 0.5rem;
  border: 1px solid var(--hdg-border);
  border-radius: 0.375rem;
}
.reference-comparison {
  th, td { padding: 0.35rem 0.5rem; font-size: inherit; vertical-align: middle; }
  th { font-weight: 400; text-transform: none; }
  th:first-child { padding-left: 0.75rem; }
  th:last-child, td:last-child { padding-right: 0.75rem; }
  tbody tr + tr th, tbody tr + tr td { border-top: 1px solid var(--hdg-border); }
  thead th { white-space: nowrap; }
  tbody th { min-width: 8rem; }
}
</style>

<style lang="scss">
@import '~@/assets/scss/variables/variables';
// The payout pages don't load SweetAlert's theme on their own; the confirmation popup needs its dark mode.
@import '@core/scss/vue/libs/vue-sweetalert.scss';

// Theme and Bootstrap status colors measure 1.9–3.7:1 against these panels.
// These shades keep text at WCAG AA (4.5:1) in both themes.
.result-reference-panel,
.reference-details-modal,
.reference-confirm-popup,
.reference-history-modal {
  --reference-success: #{darken($hdg-success, 18%)};
  --reference-warning: #{darken($hdg-warning, 30%)};
  --reference-danger: #{darken($hdg-danger, 18%)};
  --reference-link: var(--hdg-brand-primary);
}
.dark-layout .result-reference-panel,
.dark-layout .reference-details-modal,
.dark-layout .reference-confirm-popup,
.dark-layout .reference-history-modal {
  --reference-success: var(--hdg-success);
  --reference-warning: var(--hdg-warning);
  --reference-danger: #{lighten($hdg-danger, 10%)};
  --reference-link: #{lighten($hdg-dark-primary-emphasis, 6%)};
}

// Modals and the SweetAlert popup render under <body>, so these styles cannot be scoped.
.reference-details-modal,
.reference-confirm-popup,
.reference-history-modal {
  .reference-details-body { display: flex; flex-direction: column; gap: 1rem; }
  .reference-details-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .reference-details-lottery { color: var(--hdg-ink); font-size: 1.1rem; font-weight: 600; }
  .reference-details-date { color: var(--hdg-body); }
  .reference-details-empty { margin: 0; color: var(--hdg-body); }
  .reference-details-note { margin: -0.5rem 0 0; color: var(--hdg-body); font-size: 0.857rem; }
  .reference-details-note strong { color: var(--hdg-body); font-variant-numeric: tabular-nums; }
  .reference-details-alert {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    padding: 0.625rem 0.875rem;
    border-radius: 0.375rem;
    background: var(--hdg-warning-soft);
    color: var(--hdg-ink);
    font-size: 0.857rem;
    svg { flex-shrink: 0; margin-top: 0.15rem; color: var(--reference-warning); }
  }

  .reference-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .reference-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.625rem;
    border-radius: 1rem;
    font-size: 0.8rem;
    line-height: 1.5;
    white-space: nowrap;
    &[href]:hover, &[href]:focus-visible { text-decoration: underline; }
    &.is-mismatch { background: var(--hdg-danger-soft); color: var(--reference-danger); }
    &.is-agree { background: var(--hdg-success-soft); color: var(--reference-success); }
    &.is-unavailable { background: var(--hdg-warning-soft); color: var(--reference-warning); }
    &.is-neutral { background: var(--hdg-surface); color: var(--hdg-body); }
  }
  .reference-chip-meta { font-variant-numeric: tabular-nums; }

  .reference-results { margin: 0; }
  .reference-result {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    padding: 0.625rem 0;
    border-bottom: 1px solid var(--hdg-border);
    &:first-child { padding-top: 0; }
    &:last-child { border-bottom: 0; padding-bottom: 0; }
    dt { color: var(--hdg-body); font-weight: 400; white-space: nowrap; }
    dd { display: flex; flex-direction: column; align-items: flex-end; gap: 0.125rem; margin: 0; text-align: right; }
    &.is-conflict dt { color: var(--reference-danger); }
  }
  .reference-result-value {
    color: var(--hdg-ink);
    font-size: 1.35rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .reference-result-variant {
    display: flex;
    align-items: baseline;
    justify-content: flex-end;
    gap: 0.5rem;
    .reference-result-value { color: var(--reference-danger); font-size: 1.1rem; }
    small { color: var(--hdg-body); }
  }
  .reference-result-input {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    color: var(--hdg-body);
    font-size: 0.8rem;
    font-variant-numeric: tabular-nums;
    &.is-match { color: var(--reference-success); }
    &.is-mismatch { color: var(--reference-danger); font-weight: 600; }
  }

  .reference-details-fill {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
  }
  .reference-details-pick { margin-right: auto; color: var(--hdg-body); font-size: 0.857rem; }
  .reference-details-used {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: var(--reference-success);
    font-size: 0.857rem;
  }
  .reference-history-link {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0;
    color: var(--hdg-body);
    font-weight: 400;
    &:hover, &:focus-visible { color: var(--reference-link); }
  }
  // Bootstrap's secondary text is 3.7:1; keep the outline and hover wash, darken the label.
  .btn.reference-outline-button,
  .btn.reference-outline-button:not(:disabled):not(.disabled):hover,
  .btn.reference-outline-button:not(:disabled):not(.disabled):focus,
  .btn.reference-outline-button:not(:disabled):not(.disabled):active { color: var(--hdg-ink); }
}
// Vuexy's `.modal .modal-footer > .btn` adds a left margin; keep the history link on the body's text edge.
.modal.reference-details-modal .modal-footer.reference-details-footer > * { margin: 0; }

.reference-confirm-popup {
  .swal2-content { padding: 0 0.5rem; font-size: 1rem; text-align: left; }
  .reference-confirm { display: flex; flex-direction: column; gap: 1rem; }
  .reference-confirm-lead { margin: 0; color: var(--hdg-body); font-size: 0.929rem; line-height: 1.6; }
  .swal2-actions .btn { margin-top: 0; }
}

.reference-history-modal {
  .reference-history { display: flex; flex-direction: column; gap: 0.75rem; margin: 0; padding: 0; list-style: none; }
  .reference-history-item {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    border: 1px solid var(--hdg-border);
    border-radius: 0.5rem;
  }
  .reference-history-head { display: flex; flex-wrap: wrap; align-items: center; gap: 0.25rem 0.75rem; }
  .reference-history-action {
    padding: 0.125rem 0.5rem;
    border-radius: 0.25rem;
    background: var(--hdg-surface);
    color: var(--hdg-ink);
    font-size: 0.8rem;
    font-weight: 600;
  }
  .reference-history-actor { color: var(--hdg-ink); }
  .reference-history-time { margin-left: auto; color: var(--hdg-body); font-size: 0.857rem; font-variant-numeric: tabular-nums; }
  .reference-history-numbers {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1.5rem;
    margin: 0;
    dt { color: var(--hdg-body); font-size: 0.8rem; font-weight: 400; }
    dd { margin: 0; color: var(--hdg-ink); font-size: 1.1rem; font-weight: 600; letter-spacing: 0.03em; font-variant-numeric: tabular-nums; }
  }
  .reference-history-reason {
    margin: 0;
    padding: 0.5rem 0.75rem;
    border-left: 3px solid var(--reference-warning);
    background: var(--hdg-warning-soft);
    color: var(--hdg-ink);
    span { display: block; color: var(--hdg-body); font-size: 0.8rem; }
  }
}
</style>
